import { noop } from 'lodash';
import { useCallback } from 'react';

import { MedicalInformationType } from '@dreryk/egabinet-patient-medical-information-frontend-api';
import { VisitUpdateCommand } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarWarningEvent, Link } from '@dreryk/react-components';

import { MedicalImagingEntryFormBean, MedicalInformationAddEntryModalOpener } from '~legacy.patient';

import { medicalImagingTranslationKeys, visitDetailsTranslationKeys } from '../../../../translations';
import { ProcedureTableBean } from '../../tabs/procedures/procedures.types';
import { procedureLimitReached } from '../../tabs/procedures/procedures.utils';
import { VisitFormBean, VisitFormController } from '../../visitForm.types';
import { createCommonVisitParameters } from '../../visitForm.utils';

import { useVisitMedicalImagingCreateCommand } from './AddMedicalImagingLink.hooks';
import { mapMedicalImagingEntryFormBeanParametersToVisitMedicalImagingCreateParameters } from './AddMedicalImagingLink.utils';

const AVAILABLE_MEDICAL_INFORMATION_TYPES: Extract<MedicalInformationType, 'MEDICAL_IMAGING'>[] = ['MEDICAL_IMAGING'];

type AddMedicalImagingLinkProps = {
   readonly patientId: number;
   readonly visitId: number;
   readonly icon?: JSX.Element;
   readonly controller: VisitFormController;
};

// TODO: Temporary until DREGAB-18776, after that only use createVisitImaging
// eslint-disable-next-line max-lines-per-function
export const AddMedicalImagingLink = (props: AddMedicalImagingLinkProps): JSX.Element => {
   const { executeAsync: createMedicalVisitImaging } = useVisitMedicalImagingCreateCommand({
      visitId: props.visitId,
   });

   // TODO: remove DREGAB-18776
   const { executeAsync: autosave } = useCqrsCommand<void, VisitFormBean>(
      (parameters) => {
         const command = new VisitUpdateCommand();
         command.visitId = props.visitId;
         command.commonVisitParameters = createCommonVisitParameters(parameters);
         return command;
      },
      {
         onUnexpectedError: noop,
      }
   );
   // TODO: remove DREGAB-18776
   const { executeAsync: updateVisit } = useCqrsCommand<void, VisitFormBean>(
      (parameters) => {
         const command = new VisitUpdateCommand();
         command.visitId = props.visitId;
         command.commonVisitParameters = createCommonVisitParameters(parameters);
         return command;
      },
      {
         onSuccess: (result, parameters) => {
            props.controller.setValue('procedures.value', parameters.procedures.value);
         },
         onUnexpectedError: noop,
      }
   );
   const handleSuccess = useCallback(
      async (
         medicalInformationId: number,
         __: 'MEDICAL_IMAGING',
         parameters?: MedicalImagingEntryFormBean
      ): Promise<void> => {
         if (!parameters) {
            return;
         }

         const visitMedicalImgaingCreateParameters = mapMedicalImagingEntryFormBeanParametersToVisitMedicalImagingCreateParameters(
            parameters,
            medicalInformationId
         );

         // TODO: remove DREGAB-18776
         await props.controller.handleSubmitDraft(async (bean) => {
            await autosave(bean);
            await createMedicalVisitImaging(visitMedicalImgaingCreateParameters);

            const procedures = bean.procedures.value;
            const procedureExists = procedures.some((procedure) => {
               const exists = procedure.code === visitMedicalImgaingCreateParameters.icd9Code.asString();
               if (exists) {
                  procedure.quantity += 1;
                  procedure.minQuantity += 1;
               }
               return exists;
            });
            if (!procedureExists) {
               procedures.unshift({
                  code: visitMedicalImgaingCreateParameters.icd9Code.asString(),
                  name: visitMedicalImgaingCreateParameters.icd9Name.asString(),
                  minQuantity: 1,
                  quantity: 1,
               });
            }
            await updateVisit(bean);
         })();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
   );

   const handleClick = useCallback(
      (openModal: VoidFunction) => () => {
         const procedures = props.controller.watch('procedures.value');
         if (procedureLimitReached(procedures as ProcedureTableBean[])) {
            dispatchSnackbarWarningEvent(visitDetailsTranslationKeys.procedures.limitReached);
            return;
         }
         openModal();
      },
      [props.controller]
   );

   return (
      <MedicalInformationAddEntryModalOpener
         availableMedicalInformationTypes={AVAILABLE_MEDICAL_INFORMATION_TYPES}
         patientId={props.patientId}
         onSuccess={handleSuccess}
      >
         {({ openModal }) => (
            <Link
               testId={'addMedicalImagingLink'}
               onClick={handleClick(openModal)}
               label={medicalImagingTranslationKeys.emptyStatePlaceholder.addExamination}
               icon={props.icon}
            />
         )}
      </MedicalInformationAddEntryModalOpener>
   );
};
